import { useCallback, useEffect, useMemo } from 'react';
import {
	debouncedCreateAndSendShieldHeartBeatEvent,
	getIsShieldEnabled,
	ONE_DAY_IN_MILLISECONDS,
	shouldSendHeartbeat,
} from 'App/ShieldPRD/util';
import { useConfigurationContext } from 'Configuration/useConfigurationContext';
import { FeatureFlag } from 'Environment/FeatureFlag';
import { isNativeClient } from 'Environment/launchResource/device';
import { useFeatureCanary } from 'utils/useFeatureCanary';
import { useCasTelemetryAdapter } from 'Workspace/CAS/useCasTelemetryAdapter';
import { useLoadableResourceContext } from 'Workspace/ResourceProvider';
import { useUserContext } from 'Workspace/UserContext';

interface UseShieldPRDTelemetryProps<TContext = any> {
	featureFlag: FeatureFlag;
	context: TContext;
	getExtraPayload?: (ctx: TContext) => Promise<Record<string, any>> | Record<string, any>;
}

export const useShieldPRDTelemetry = ({
	featureFlag,
	context,
	getExtraPayload,
}: UseShieldPRDTelemetryProps) => {
	const resourceContext = useLoadableResourceContext();
	const userContext = useUserContext();
	const { workspaceConfiguration } = useConfigurationContext();
	const CASEventPublisher = useCasTelemetryAdapter();
	const { userDetails, hasLoggedIn } = userContext;
	const isShieldEnabledFeature = useFeatureCanary(featureFlag);

	const shouldDisableShieldPrd = useMemo(() => {
		return (
			isNativeClient() ||
			!isShieldEnabledFeature ||
			resourceContext.loading ||
			userContext.loading ||
			IS_ON_PREM
		);
	}, [isShieldEnabledFeature, resourceContext.loading, userContext.loading]);

	const sendTelemetry = useCallback(async () => {
		const isShieldEnabled = getIsShieldEnabled(
			workspaceConfiguration,
			context?.leasingProperties
		);

		const extraPayload =
			typeof getExtraPayload === 'function' ? await getExtraPayload(context) : {};

		const eventPayload = {
			CASEventPublisher,
			resources: resourceContext?.value?.resources,
			userDisplayName: userDetails?.userDisplayName,
			storeGuid: workspaceConfiguration.storeIdentifiers?.storeGuid,
			userId: userDetails?.userId,
			isShieldEnabled,
			...extraPayload,
		};

		await debouncedCreateAndSendShieldHeartBeatEvent(eventPayload);
	}, [
		workspaceConfiguration,
		context,
		getExtraPayload,
		CASEventPublisher,
		resourceContext?.value?.resources,
		userDetails?.userDisplayName,
		userDetails?.userId,
	]);

	useEffect(() => {
		if (shouldDisableShieldPrd) {
			return undefined;
		}

		const validateAndSendTelemetry = async () => {
			const isLoggedIn = await hasLoggedIn();
			if (
				!isLoggedIn ||
				userDetails?.userDisplayName === null ||
				!shouldSendHeartbeat()
			) {
				return;
			}

			await sendTelemetry();
		};

		validateAndSendTelemetry();
		const intervalId = setInterval(validateAndSendTelemetry, ONE_DAY_IN_MILLISECONDS);
		return () => clearInterval(intervalId);
	}, [shouldDisableShieldPrd, userDetails?.userDisplayName, hasLoggedIn, sendTelemetry]);
};
